import type { LocalizedOut, OnboardingFieldOut, TemplateOut } from "@sagot/api-client";

/**
 * Template ranking over template DATA (mirrors backend `app/services/tenancy/profile.py`):
 * templates offered for the industry, best goal overlap first; ties go to the industry's primary
 * template (`recommended_for`), then catalogue order.
 */
export function rankTemplates(templates: readonly TemplateOut[], industry: string | null, goals: readonly string[] = []): TemplateOut[] {
  const wanted = new Set(goals);
  const offered = templates.filter((template) => industry === null || template.industries.includes(industry));
  const score = (template: TemplateOut) => template.goals.filter((goal) => wanted.has(goal)).length;
  const primary = (template: TemplateOut) => (industry !== null && template.recommended_for.includes(industry) ? 0 : 1);
  return [...offered].sort((a, b) => score(b) - score(a) || primary(a) - primary(b) || a.sort_order - b.sort_order);
}

export function recommendedTemplate(templates: readonly TemplateOut[], industry: string | null, goals: readonly string[] = []): TemplateOut | undefined {
  return rankTemplates(templates, industry, goals)[0];
}

/** The industry's primary template, else the best-ranked offer. */
export function primaryTemplate(templates: readonly TemplateOut[], industry: string | null): TemplateOut | undefined {
  const sorted = [...templates].sort((a, b) => a.sort_order - b.sort_order);
  return sorted.find((template) => industry !== null && template.recommended_for.includes(industry)) ?? recommendedTemplate(templates, industry);
}

/** Industry follow-up questions = the primary template's `onboarding_fields`. */
export function fieldsForIndustry(templates: readonly TemplateOut[], industry: string | null): OnboardingFieldOut[] {
  return primaryTemplate(templates, industry)?.onboarding_fields ?? [];
}

/**
 * Follow-up questions of a setup: the industry's primary template's questions plus the chosen
 * template's own, deduplicated by key in first-seen order (the chosen template's definition wins
 * on a shared key). Mirrors backend `fields_for_template` (app/services/tenancy/profile.py).
 */
export function fieldsForTemplate(templates: readonly TemplateOut[], industry: string | null, templateKey: string | null | undefined): OnboardingFieldOut[] {
  const merged = new Map(fieldsForIndustry(templates, industry).map((field) => [field.key, field]));
  const chosen = templates.find((template) => template.key === templateKey);
  for (const field of chosen?.onboarding_fields ?? []) merged.set(field.key, field);
  return [...merged.values()];
}

export interface TemplateOption {
  key: string;
  name: string;
}

/**
 * What the step-3 "Purpose template" switcher offers: the templates offered for the industry,
 * ranked by industry + goals (see `rankTemplates`), with the template in use always listed
 * (an AI draft or an industry change may have left it outside the ranking).
 */
export function switcherOptions(templates: readonly TemplateOut[], industry: string | null, goals: readonly string[], current: string): TemplateOption[] {
  const ranked = rankTemplates(templates, industry, goals);
  const active = ranked.some((template) => template.key === current) ? [] : [templates.find((template) => template.key === current) ?? { key: current, name: current }];
  return [...active, ...ranked].map(({ key, name }) => ({ key, name }));
}

export function localized(text: LocalizedOut | null | undefined, language: string): string {
  if (!text) return "";
  return language === "fil" ? text.fil || text.en : text.en;
}

/** Help line for a field: the country-specific one (e.g. PH examples) wins. */
export function fieldHelp(field: OnboardingFieldOut, country: string, language: string): string {
  return localized(field.country_help?.[country] ?? field.help, language);
}

export type ProfileAnswers = Record<string, string | string[]>;

/** Required questions still unanswered (keys). */
export function missingAnswers(fields: readonly OnboardingFieldOut[], answers: ProfileAnswers): string[] {
  return fields
    .filter((field) => field.required)
    .filter((field) => {
      const value = answers[field.key];
      return Array.isArray(value) ? value.length === 0 : !value?.trim();
    })
    .map((field) => field.key);
}

function answerFor(field: OnboardingFieldOut, value: string | string[]): string | string[] | undefined {
  if (field.type === "text") return typeof value === "string" ? value : undefined;
  const allowed = new Set((field.options ?? []).map((option) => option.value));
  if (field.type === "select") return typeof value === "string" && allowed.has(value) ? value : undefined;
  const picked = (Array.isArray(value) ? value : [value]).filter((choice) => allowed.has(choice));
  return picked.length ? picked : undefined;
}

/**
 * The answers that still apply to `fields`: only their keys, and only values their options
 * allow (switching industry or template drops what no longer fits and keeps the rest).
 */
export function answersFor(fields: readonly OnboardingFieldOut[], answers: ProfileAnswers): ProfileAnswers {
  const kept: ProfileAnswers = {};
  for (const field of fields) {
    const stored = answers[field.key];
    const value = stored === undefined ? undefined : answerFor(field, stored);
    if (value !== undefined) kept[field.key] = value;
  }
  return kept;
}

const ORG_NAME_VARIABLE = "{{org_name}}";

/** Template copy keeps the platform's `{{org_name}}` variable; previews show the real (or a neutral) name. */
export function fillOrgName(text: string, orgName: string): string {
  return text.replaceAll(ORG_NAME_VARIABLE, orgName);
}
